/**
 * Similar to `keyof T`, but removes a generic object's number and Symbol key types,
 * so its string keys can be used in places expecting a string type.
 * For example, if T is { foo: number, bar: string }, StringKeysOfObject<T> is "foo" | "bar".
 */
type StringKeysOfObjectInternal<T, K extends keyof T> = (K extends string ? K : never);
export type StringKeysOfObject<T> = StringKeysOfObjectInternal<T, keyof T>;

/**
 * Use in the default clause of a switch statement for a compile-time check that all the cases have been covered
 */
/* istanbul ignore next */
export function assertUnreachable(_: never) {
  return;
}

/**
 * A map type whose key and value must match. The type parameter U should be a string union type.
 * This is useful when defining "enums" to use at runtime based on string union types,
 * since declaring the source type as a string union allows this type safety for the enum mapping,
 * as well as allowing types to be extended from one another.
 */
export type StringUnionKeyAsValue<U extends string> = Readonly<{
  [K in U]: K
}>;

export function filterOutNullAndUndefined<T>(arr: ReadonlyArray<(T | null | undefined)>): ReadonlyArray<T> {
  return arr.filter(t => t !== null && t !== undefined) as ReadonlyArray<T>;
}

export function createErrorFunction<T>(message: string): (...args: unknown[]) => T {
  /* istanbul ignore next */
  const errorFunction: (...args: unknown[]) => T = () => { throw new Error(message); };
  return errorFunction;
}


/**
 * This mapped type removes null and undefined from the possible values of its members.
 * Example: WithNonNullableMembers<{ foo: string | null }> becomes { foo: string }
 */
export type WithNonNullableMembers<T> = {
  [P in keyof T]: NonNullable<T[P]>;
};

/**
 * Returns true iff all the params are not null and not undefined, and also supplies
 * a type guard to let callers be aware of this
 */
export function membersAreDefined<T>(params: T): params is WithNonNullableMembers<T> {
  for (const key in params) {
    if (params[key] === null && params[key] === undefined) {
      return false;
    }
  }
  return true;
}
